<template>
  <div class="subscriptions-management">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-package-variant</v-icon>
                Subscriptions Management
                <span class="page-inline-subtitle">Manage subscription plans for AI agents marketplace</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  color="primary" 
                  to="/subscriptions/create"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Subscription
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Subscriptions Grid -->
      <v-row>
        <v-col
          v-for="subscription in subscriptions"
          :key="subscription.id"
          cols="12"
          sm="6"
          lg="4"
        >
          <v-card class="subscription-card" elevation="8">
            <div class="subscription-header" :style="`background: ${subscription.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}`">
              <div class="plan-badge" v-if="subscription.is_popular">
                <v-icon small>mdi-star</v-icon>
                Popular
              </div>
              <div class="plan-name">{{ subscription.name }}</div>
              <div class="plan-description">{{ subscription.description }}</div>
              <div class="plan-pricing">
                <div class="price-display">
                  <span class="currency">$</span>
                  <span class="price">{{ subscription.monthly_price || 0 }}</span>
                  <span class="period">/month</span>
                </div>
                <div class="annual-price" v-if="subscription.annual_price">
                  ${{ subscription.annual_price }}/year
                </div>
              </div>
            </div>

            <v-card-text class="subscription-content pa-4">
              <!-- AI Agents List -->
              <div class="agents-section mb-4">
                <h4 class="section-title">
                  <v-icon small class="mr-2">mdi-robot</v-icon>
                  AI Agents ({{ subscription.agents?.length || 0 }})
                </h4>
                <div class="agents-list">
                  <div
                    v-for="agent in subscription.agents"
                    :key="agent.id"
                    class="agent-item"
                  >
                    <v-avatar size="24" class="mr-2">
                      <v-img
                        :src="agent.ai_agent?.logo_url || require('../../assets/sapp-only-logo.png')"
                        :alt="agent.ai_agent?.name"
                      />
                    </v-avatar>
                    <span class="agent-name">{{ agent.ai_agent?.name || 'ContentMod AI' }}</span>
                    <v-spacer></v-spacer>
                    <span class="agent-price">${{ agent.ai_agent?.monthly_price || 0 }}</span>
                  </div>
                </div>
              </div>

              <!-- Cost Breakdown -->
              <div class="cost-breakdown mb-4">
                <div class="cost-item">
                  <span>Individual Agent Cost:</span>
                  <span>${{ subscription.total_agent_cost || 0 }}</span>
                </div>
                <div class="cost-item final-price">
                  <span>Subscription Price:</span>
                  <span>${{ subscription.monthly_price || 0 }}</span>
                </div>
              </div>

              <!-- Features -->
              <div class="features-section" v-if="subscription.features?.length">
                <h4 class="section-title">
                  <v-icon small class="mr-2">mdi-check-circle</v-icon>
                  Features
                </h4>
                <ul class="features-list">
                  <li v-for="feature in subscription.features" :key="feature.id">
                    <v-icon small color="success">mdi-check</v-icon>
                    {{ feature.name }}
                  </li>
                </ul>
              </div>
            </v-card-text>

            <v-card-actions class="subscription-actions pa-4">
              <div class="action-buttons">
                <v-btn
                  small
                  outlined
                  color="primary"
                  :to="`/subscriptions/${subscription.id}`"
                >
                  <v-icon left small>mdi-eye</v-icon>
                  View
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  :to="`/subscriptions/${subscription.id}/edit`"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  Edit
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="success"
                  @click="assignToCompany(subscription)"
                >
                  <v-icon left small>mdi-account-plus</v-icon>
                  Assign
                </v-btn>
              </div>
              <div class="status-section">
                <v-chip
                  :color="subscription.is_active ? 'success' : 'error'"
                  dark
                  small
                >
                  {{ subscription.is_active ? 'Active' : 'Inactive' }}
                </v-chip>
              </div>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <div v-if="!isLoading && subscriptions.length === 0" class="empty-state">
        <v-icon size="64" color="#64748b">mdi-package-variant-closed</v-icon>
        <h2>No Subscriptions Found</h2>
        <p>Create your first subscription plan to get started</p>
        <v-btn color="primary" to="/subscriptions/create" class="mt-4">
          <v-icon left>mdi-plus</v-icon>
          Create Subscription
        </v-btn>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p>Loading subscriptions...</p>
      </div>
    </v-container>

    <!-- Assign to Company Dialog -->
    <v-dialog v-model="showAssignDialog" max-width="800px" persistent>
      <v-card class="assign-dialog" elevation="24">
        <v-card-title class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-section">
              <h2 class="dialog-title">Assign Subscription</h2>
              <p class="dialog-subtitle">Assign "{{ selectedSubscription?.name }}" to a company</p>
            </div>
            <v-btn 
              icon 
              @click="closeAssignDialog"
              class="close-btn"
            >
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="dialog-content pa-6">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="assignForm.company_id"
                :items="companies"
                outlined
                label="Select Company *"
                prepend-inner-icon="mdi-office-building"
                :error-messages="assignErrors.company_id"
                class="minimal-input"
                dense
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="assignForm.start_date"
                outlined
                label="Start Date *"
                prepend-inner-icon="mdi-calendar"
                :error-messages="assignErrors.start_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="assignForm.end_date"
                outlined
                label="End Date"
                prepend-inner-icon="mdi-calendar"
                :error-messages="assignErrors.end_date"
                class="minimal-input"
                dense
                type="date"
                hide-details="auto"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-autocomplete
                v-model="assignForm.coupon_code"
                :items="coupons"
                outlined
                label="Apply Coupon (Optional)"
                prepend-inner-icon="mdi-tag"
                class="minimal-input"
                dense
                clearable
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <v-switch
                v-model="assignForm.is_custom"
                inset
                color="primary"
                label="Custom Subscription"
                class="minimal-switch"
                hide-details
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Custom Agent Selection -->
          <div v-if="assignForm.is_custom" class="custom-agents-section mt-4">
            <h4 class="section-title mb-3">
              <v-icon small class="mr-2">mdi-robot-excited</v-icon>
              Select AI Agents
            </h4>
            <div class="agents-grid">
              <div
                v-for="agent in availableAgents"
                :key="agent.id"
                class="agent-select-card"
                :class="{ 'selected': assignForm.selected_agents.includes(agent.id) }"
                @click="toggleAgent(agent.id)"
              >
                <v-checkbox
                  :value="assignForm.selected_agents.includes(agent.id)"
                  @click.stop="toggleAgent(agent.id)"
                  color="primary"
                  hide-details
                  class="agent-checkbox"
                ></v-checkbox>
                <v-avatar size="32" class="mb-2">
                  <v-img
                    :src="agent.logo_url || require('../../assets/sapp-only-logo.png')"
                    :alt="agent.name"
                  />
                </v-avatar>
                <div class="agent-name">{{ agent.name }}</div>
                <div class="agent-price">${{ agent.monthly_price || 0 }}/mo</div>
              </div>
            </div>
            <div class="custom-total mt-3">
              <strong>Total Cost: ${{ calculateCustomTotal() }}/month</strong>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeAssignDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="assignSubscription"
            :loading="isAssigning"
            class="save-btn"
          >
            Assign Subscription
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SubscriptionsPage",
  data() {
    return {
      subscriptions: [],
      isLoading: false,
      showAssignDialog: false,
      selectedSubscription: null,
      isAssigning: false,
      companies: [],
      coupons: [],
      availableAgents: [],
      assignForm: {
        company_id: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        coupon_code: null,
        is_custom: false,
        selected_agents: [],
      },
      assignErrors: {},
    };
  },
  mounted() {
    this.getData();
    this.getCompanies();
    this.getCoupons();
    this.getAvailableAgents();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const response = await axios.get('/subscription-plans');
        this.subscriptions = response.data.data;
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
        // Show empty state if no data from API
        this.subscriptions = [];
      }
      this.isLoading = false;
    },
    async getCompanies() {
      try {
        const response = await axios.get('/companies');
        this.companies = response.data.data.map(company => ({
          text: company.name,
          value: company.id
        }));
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    },
    async getCoupons() {
      try {
        const response = await axios.get('/coupons');
        this.coupons = response.data.data.map(coupon => ({
          text: `${coupon.code} (${coupon.type === 'percent' ? coupon.value + '%' : '$' + coupon.value} off)`,
          value: coupon.code
        }));
      } catch (error) {
        console.error('Error fetching coupons:', error);
        // Fallback to sample coupon data
        this.coupons = [
          { text: 'WELCOME20 (20% off)', value: 'WELCOME20' },
          { text: 'SAVE50 ($50 off)', value: 'SAVE50' },
          { text: 'FIRST30 (30% off)', value: 'FIRST30' },
          { text: 'STUDENT15 (15% off)', value: 'STUDENT15' },
          { text: 'ENTERPRISE100 ($100 off)', value: 'ENTERPRISE100' },
          { text: 'EARLY25 (25% off)', value: 'EARLY25' },
        ];
      }
    },
    async getAvailableAgents() {
      try {
        const response = await axios.get('/ai-agents?is_active=true');
        this.availableAgents = response.data.data;
      } catch (error) {
        console.error('Error fetching agents:', error);
        // Show empty state if no agents from API
        this.availableAgents = [];
      }
    },
    assignToCompany(subscription) {
      this.selectedSubscription = subscription;
      this.assignForm = {
        company_id: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        coupon_code: null,
        is_custom: false,
        selected_agents: subscription.agents?.map(agent => agent.ai_agent_id) || [],
      };
      this.assignErrors = {};
      this.showAssignDialog = true;
    },
    closeAssignDialog() {
      this.showAssignDialog = false;
      this.selectedSubscription = null;
      this.assignForm = {
        company_id: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        coupon_code: null,
        is_custom: false,
        selected_agents: [],
      };
      this.assignErrors = {};
    },
    toggleAgent(agentId) {
      const index = this.assignForm.selected_agents.indexOf(agentId);
      if (index > -1) {
        this.assignForm.selected_agents.splice(index, 1);
      } else {
        this.assignForm.selected_agents.push(agentId);
      }
    },
    calculateCustomTotal() {
      return this.assignForm.selected_agents.reduce((total, agentId) => {
        const agent = this.availableAgents.find(a => a.id === agentId);
        return total + (agent?.monthly_price || 0);
      }, 0);
    },
    async assignSubscription() {
      try {
        this.isAssigning = true;
        this.assignErrors = {};
        
        const payload = {
          subscription_id: this.selectedSubscription.id,
          ...this.assignForm,
        };

        await axios.post('/company-subscriptions', payload);
        this.closeAssignDialog();
        this.$emit('subscription-assigned');
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          this.assignErrors = error.response.data.errors;
        }
        console.error('Error assigning subscription:', error);
      }
      this.isAssigning = false;
    },
  },
};
</script>

<style scoped>
.subscriptions-management {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* Subscription Cards */
.subscription-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.subscription-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2) !important;
}

.subscription-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  position: relative;
}

.plan-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.plan-name {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.plan-description {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 16px;
}

.plan-pricing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.price-display {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.currency {
  font-size: 20px;
  font-weight: 600;
}

.price {
  font-size: 32px;
  font-weight: 700;
}

.period {
  font-size: 16px;
  opacity: 0.8;
}

.annual-price {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 4px;
}

.subscription-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

.agents-list {
  max-height: 200px;
  overflow-y: auto;
}

.agent-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.agent-item:last-child {
  border-bottom: none;
}

.agent-name {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.agent-price {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

.cost-breakdown {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;
}

.cost-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 14px;
}

.cost-item.final-price {
  font-weight: 600;
  color: #2c3e50;
  border-top: 1px solid #e2e8f0;
  padding-top: 8px;
  margin-top: 8px;
  margin-bottom: 0;
}


.features-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.features-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 14px;
  color: #2c3e50;
}

.subscription-actions {
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons .v-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-state h2 {
  margin: 16px 0 8px 0;
  color: #2c3e50;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* Header Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Assign Dialog Styling */
.assign-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 24px !important;
  border-radius: 0 !important;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.dialog-title-section {
  flex: 1;
}

.dialog-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.dialog-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}

.dialog-content {
  background: #f8f9fa;
}

/* Custom Agents Section */
.custom-agents-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e2e8f0;
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.agent-select-card {
  background: #f8f9fa;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.agent-select-card:hover {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.agent-select-card.selected {
  border-color: #667eea;
  background: rgba(102, 126, 234, 0.1);
}

.agent-checkbox {
  position: absolute;
  top: 8px;
  right: 8px;
}

.agent-select-card .agent-name {
  font-size: 12px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.agent-select-card .agent-price {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.custom-total {
  text-align: center;
  font-size: 16px;
  color: #2c3e50;
  background: #e8f4fd;
  border-radius: 8px;
  padding: 12px;
  border: 2px solid #667eea;
}

/* Minimal Input Styling */
.minimal-input {
  margin-bottom: 8px;
}

.minimal-input >>> .v-input__control {
  min-height: 56px;
}

.minimal-input >>> .v-input__control .v-input__slot {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  padding: 0 16px !important;
  margin: 0 !important;
  box-shadow: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:before,
.minimal-input >>> .v-input__control .v-input__slot:after {
  display: none !important;
}

.minimal-input >>> .v-input__control .v-input__slot:hover {
  border-color: #cbd5e1 !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.minimal-input >>> .v-label {
  color: #64748b !important;
  font-weight: 500;
  font-size: 14px;
}

.minimal-input >>> .v-label--active {
  color: #667eea !important;
  transform: translateY(-18px) scale(0.85) !important;
}

.minimal-input >>> input {
  color: #1e293b !important;
  font-weight: 500;
  font-size: 15px;
  padding: 8px 0 !important;
}

.minimal-input >>> .v-input__prepend-inner .v-icon {
  color: #667eea !important;
  margin-right: 12px;
}

/* Remove fieldset borders */
.minimal-input >>> fieldset {
  display: none !important;
}

/* Switch Styling */
.minimal-switch >>> .v-input--switch__thumb {
  color: #667eea !important;
}

.minimal-switch >>> .v-input--switch__track {
  color: #e2e8f0 !important;
}

.minimal-switch >>> .v-input--switch.v-input--is-label-active .v-input--switch__track {
  color: #667eea !important;
}

.minimal-switch >>> .v-label {
  color: #2c3e50 !important;
  font-weight: 500;
  font-size: 15px;
}

.dialog-actions {
  background: #f8f9fa;
  border-top: 1px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  color: #64748b !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.cancel-btn:hover {
  background: rgba(100, 116, 139, 0.1) !important;
}

.save-btn {
  background: #667eea !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.save-btn:hover {
  background: #5a6fd8 !important;
}

.save-btn:before {
  background-color: transparent;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .agents-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .subscription-header {
    padding: 20px;
  }
  
  .plan-name {
    font-size: 20px;
  }
  
  .price {
    font-size: 28px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
  
  .action-buttons .v-btn {
    width: 100%;
  }
  
  .agents-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}
</style>